import { useState, useCallback, useMemo } from 'react'
import { Head, Link } from '@inertiajs/react'
import AppLayout from '@/layouts/AppLayout'
import AdminLayout from '@/components/admin/AdminLayout'
import PageHeader from '@/components/ui/PageHeader'
import Notification from '@/components/ui/Notification'
import DataTable, { useFlash, usePagination, type DataTableColumn } from '@/components/ui/DataTable'
import { FormSelect, FormInput } from '@/components/ui/form'
import Button from '@/components/ui/Button'
import Pagination from '@/components/ui/Pagination'
import { Badge } from '@/components/catalyst/badge'
import { Dialog, DialogTitle, DialogBody, DialogActions } from '@/components/catalyst/dialog'
import { PencilIcon, TrashIcon, UsersIcon, XMarkIcon } from '@heroicons/react/24/outline'

// ─── Types ──────────────────────────────────────

interface UserRow {
  id: number
  name: string | null
  email: string
  role_id: number | null
  role_name: string | null
  sign_in_count: number
  last_sign_in_at: string | null
}

interface RoleOption { id: number; name: string }

interface Props {
  users: UserRow[]
  all_roles: RoleOption[]
}

const PAGE_SIZES = [30, 50, 100, 200]

// ─── Component ──────────────────────────────────

export default function UsersIndex({ users: initialUsers, all_roles }: Props) {
  const [users, setUsers] = useState(initialUsers)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [formName, setFormName] = useState('')
  const [formEmail, setFormEmail] = useState('')
  const [formRoleId, setFormRoleId] = useState('')
  const [formPassword, setFormPassword] = useState('')
  const [formPasswordConfirmation, setFormPasswordConfirmation] = useState('')
  const [formErrors, setFormErrors] = useState<string[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [notifMessage, setNotifMessage] = useState('')
  const [showNotif, setShowNotif] = useState(false)
  const { flashRow } = useFlash()

  // URL param filtering for role_id (e.g. from Roles/Show "X users" link)
  const url = typeof window !== 'undefined' ? new URL(window.location.href) : null
  const filterRoleId = url?.searchParams.get('role_id')
  const filteredByRole = useMemo(() => {
    if (!filterRoleId) return users
    return users.filter(u => String(u.role_id) === filterRoleId)
  }, [users, filterRoleId])
  const filterRoleName = filterRoleId ? all_roles.find(r => String(r.id) === filterRoleId)?.name : null

  const csrfToken = typeof document !== 'undefined'
    ? document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
    : ''
  const notify = useCallback((msg: string) => { setNotifMessage(msg); setShowNotif(true) }, [])

  const pageSizeOptions = PAGE_SIZES.map(n => ({ value: String(n), label: `${n} per page` }))
  const { pageSize, setPageSize, currentPage, setCurrentPage, totalPages, paginatedRows, totalCount } = usePagination(filteredByRole)

  const roleOptions = all_roles.map(r => ({ value: String(r.id), label: r.name }))

  // ─── CRUD ───────────────────────────────────
  const openCreate = () => {
    setFormName(''); setFormEmail(''); setFormRoleId('')
    setFormPassword(''); setFormPasswordConfirmation(''); setFormErrors([]); setDialogOpen(true)
  }

  const handleSubmit = async () => {
    if (!formEmail.trim()) { setFormErrors(['Email is required']); return }
    setFormErrors([]); setSubmitting(true)
    try {
      const body: Record<string, string | number | null> = {
        name: formName.trim(), email: formEmail.trim(),
        role_id: formRoleId ? Number(formRoleId) : null,
      }
      if (formPassword) {
        body.password = formPassword
        body.password_confirmation = formPasswordConfirmation
      }
      const res = await fetch('/admin/users.json', {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
        body: JSON.stringify({ user: body }),
      })
      if (res.ok) {
        const data = await res.json()
        setUsers(prev => [data, ...prev])
        setDialogOpen(false)
        notify('Saved:User created')
      } else {
        const err = await res.json().catch(() => ({}))
        setFormErrors(err.errors || ['Failed to save'])
      }
    } catch { setFormErrors(['Network error']) }
    setSubmitting(false)
  }

  const deleteUser = useCallback(async (id: number) => {
    if (!confirm('Are you sure you want to delete this user?')) return
    try {
      const res = await fetch(`/admin/users/${id}.json`, { method: 'DELETE', headers: { 'X-CSRF-Token': csrfToken } })
      if (res.ok) { setUsers(prev => prev.filter(u => u.id !== id)); notify('Deleted:User removed') }
    } catch { /* silent */ }
  }, [csrfToken, notify])

  // ─── Columns ────────────────────────────────
  const columns: DataTableColumn<UserRow>[] = [
    {
      key: 'name', label: 'Name',
      render: row => (
        <Link href={`/admin/users/${row.id}`} className="font-medium text-indigo-600 hover:text-indigo-500 hover:underline dark:text-indigo-400">
          {row.name || '-'}
        </Link>
      ),
      sortValue: row => row.name || '',
      filterValue: row => row.name || '',
    },
    {
      key: 'email', label: 'Email',
      render: row => <span>{row.email}</span>,
      sortValue: row => row.email,
      filterValue: row => row.email,
    },
    {
      key: 'role_name', label: 'Role',
      render: row => row.role_name ? <Badge color="indigo">{row.role_name}</Badge> : <span className="text-zinc-400">-</span>,
      sortValue: row => row.role_name || '',
      filterValue: row => row.role_name || '',
    },
    {
      key: 'sign_in_count', label: 'Sign-in Count',
      render: row => <span>{row.sign_in_count}</span>,
      sortValue: row => row.sign_in_count,
    },
    {
      key: 'last_sign_in_at', label: 'Last Sign In',
      render: row => <span className="text-zinc-500">{row.last_sign_in_at || 'Never'}</span>,
      sortValue: row => row.last_sign_in_at || '',
    },
  ]

  return (
    <>
      <Head title="Admin - Users" />
      <Notification message={notifMessage} show={showNotif} onClose={() => setShowNotif(false)} />
      <AdminLayout>
      <main>
      <PageHeader title="Users" icon={<UsersIcon className="h-5 w-5" />} actions={
        <div className="flex items-center gap-2">
          <div className="w-36"><FormSelect value={String(pageSize)} onChange={v => { setPageSize(Number(v)); setCurrentPage(0) }} options={pageSizeOptions} /></div>
          <Button onClick={openCreate}>+ New User</Button>
        </div>
      } />

      {filterRoleId && (
        <div className="mb-2 flex items-center gap-2 px-4 sm:px-6 lg:px-8">
          <Badge color="indigo">
            <span className="inline-flex items-center gap-1 text-xs font-medium">
              Filtered by role: {filterRoleName || `ID ${filterRoleId}`}
              <Link href="/admin/users" className="ml-1 inline-flex rounded hover:text-indigo-800 dark:hover:text-indigo-200">
                <XMarkIcon className="h-3.5 w-3.5" />
              </Link>
            </span>
          </Badge>
        </div>
      )}

      <div className="mb-2"><Badge color="zinc"><span className="text-xs font-medium">{totalCount} users</span></Badge></div>

      <DataTable
        rows={paginatedRows} columns={columns} rowKey={row => row.id}
        flashRowId={flashRow?.id ?? null} flashStatus={flashRow?.status ?? null}
        emptyText="No users" filteredEmptyText="No users match your filters"
        actions={row => (
          <div className="flex items-center justify-end gap-1">
            <Link href={`/admin/users/${row.id}`} className="rounded p-1 text-zinc-400 hover:text-indigo-600 dark:text-zinc-500 dark:hover:text-indigo-400" title="Edit"><PencilIcon className="h-4 w-4" /></Link>
            <button onClick={() => deleteUser(row.id)} className="rounded p-1 text-zinc-400 hover:text-red-600 dark:text-zinc-500 dark:hover:text-red-400" title="Delete"><TrashIcon className="h-4 w-4" /></button>
          </div>
        )}
      />

      <Pagination currentPage={currentPage} totalPages={totalPages} pageSize={pageSize} totalCount={totalCount} onPageChange={setCurrentPage} />

      {/* Create Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>New User</DialogTitle>
        <DialogBody>
          {formErrors.length > 0 && (
            <div className="mb-3 rounded-lg border border-red-200 bg-red-50 p-2 dark:border-red-800 dark:bg-red-900/20">
              <ul className="list-disc pl-4 text-xs text-red-700 dark:text-red-400">{formErrors.map((e, i) => <li key={i}>{e}</li>)}</ul>
            </div>
          )}
          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-zinc-700 dark:text-zinc-300">Name</label>
              <FormInput value={formName} onChange={e => setFormName((e.target as HTMLInputElement).value)} placeholder="Full name..." />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-zinc-700 dark:text-zinc-300">Email *</label>
              <FormInput value={formEmail} onChange={e => setFormEmail((e.target as HTMLInputElement).value)} placeholder="user@example.com" autoFocus />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-zinc-700 dark:text-zinc-300">Role</label>
              <FormSelect value={formRoleId} onChange={v => setFormRoleId(v)} options={roleOptions} placeholder="Select role..." />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-zinc-700 dark:text-zinc-300">Password *</label>
              <FormInput type="password" value={formPassword} onChange={e => setFormPassword((e.target as HTMLInputElement).value)} placeholder="Password..." />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-zinc-700 dark:text-zinc-300">Password Confirmation</label>
              <FormInput type="password" value={formPasswordConfirmation} onChange={e => setFormPasswordConfirmation((e.target as HTMLInputElement).value)} placeholder="Confirm password..." />
            </div>
          </div>
        </DialogBody>
        <DialogActions>
          <Button variant="ghost" onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button disabled={submitting} onClick={handleSubmit}>{submitting ? 'Saving...' : 'Create'}</Button>
        </DialogActions>
      </Dialog>
      </main>
      </AdminLayout>
    </>
  )
}

UsersIndex.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
